<template>
  <!-- Schwebender Einstieg zum KARS-KI Support-Assistenten. Sitzt über dem
       Knopf „Fehler melden“ (BugReportButton) und respektiert wie dieser
       --app-fab-lift für feste Fußleisten. Strg/⌘ + J öffnet das Panel. -->
  <div class="ai-launcher" :class="{ 'ai-launcher--open': store.isOpen }">
    <Transition name="ai-label">
      <span v-if="showLabel && !store.isOpen" class="ai-launcher__label">{{ $gt('KARS-KI fragen') }}</span>
    </Transition>

    <button
      aria-controls="kars-ai-panel"
      :aria-expanded="store.isOpen"
      :aria-label="store.isOpen ? $gt('KARS-KI schließen') : $gt('KARS-KI öffnen')"
      class="ai-launcher__button"
      data-testid="ai-launcher"
      type="button"
      @blur="showLabel = false"
      @click="store.toggle()"
      @focus="showLabel = true"
      @mouseenter="showLabel = true"
      @mouseleave="showLabel = false"
    >
      <span v-if="hasUnseen" aria-hidden="true" class="ai-launcher__ring" :class="`is-${store.highestSeverity}`" />
      <span class="ai-orb ai-launcher__orb" :class="{ 'is-still': store.isOpen }">
        <Transition mode="out-in" name="ai-icon">
          <X v-if="store.isOpen" key="close" :size="22" />
          <Sparkles v-else key="open" :size="22" />
        </Transition>
      </span>
      <span
        v-if="hasUnseen && !store.isOpen"
        class="ai-launcher__badge"
        :class="`is-${store.highestSeverity}`"
        :title="$gt('Neue Hinweise für Sie')"
      >
        {{ store.unseenInsights.length }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
  import { Sparkles, X } from 'lucide-vue-next'
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
  import { useAssistantStore } from '@/stores/assistantStore'
  import './assistant.css'

  const store = useAssistantStore()
  const showLabel = ref(false)
  const hasUnseen = computed(() => store.unseenInsights.length > 0)

  function onKeydown (event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'j') {
      event.preventDefault()
      store.toggle()
    }
  }

  onMounted(() => {
    window.addEventListener('keydown', onKeydown)
    // Kontext (Hinweise) früh laden, damit das Badge erscheinen kann – ohne
    // das Panel zu öffnen.
    void store.ensureContext()
  })

  onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<style scoped>

  .ai-launcher {
    position: fixed;
    right: 24px;
    /* Über dem Knopf „Fehler melden“ (44 px hoch, 24 px Rand). */
    bottom: calc(84px + var(--app-fab-lift, 0px));
    z-index: var(--ai-z);
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .ai-launcher__button {
    position: relative;
    display: grid;
    place-items: center;
    width: 60px;
    height: 60px;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: transparent;
    cursor: pointer;
  }

  .ai-launcher__orb {
    width: 56px;
    height: 56px;
    animation: ai-flow 12s ease-in-out infinite, ai-breathe 4.2s ease-in-out infinite;
    transition: transform 220ms var(--ai-ease), box-shadow 220ms var(--ai-ease);
  }

  .ai-launcher__orb.is-still {
    animation: ai-flow 12s ease-in-out infinite;
  }

  .ai-launcher__button:hover .ai-launcher__orb {
    transform: scale(1.06);
    box-shadow: 0 16px 36px rgba(79, 70, 229, 0.46), inset 0 1px 0 rgba(255, 255, 255, 0.35);
  }

  .ai-launcher__button:active .ai-launcher__orb {
    transform: scale(0.96);
  }

  .ai-launcher__ring {
    position: absolute;
    inset: -3px;
    border-radius: 999px;
    background: conic-gradient(from 0deg, transparent 0 60%, var(--ai-violet) 85%, #fff 100%);
    animation: ai-ring-spin 2.4s linear infinite;
    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px));
    mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px));
  }

  .ai-launcher__ring.is-warn {
    background: conic-gradient(from 0deg, transparent 0 60%, var(--ai-warn) 85%, #fff 100%);
  }

  .ai-launcher__badge {
    position: absolute;
    top: 0;
    right: 0;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    border: 2px solid #fff;
    background: var(--ai-hint);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 16px;
    text-align: center;
    animation: ai-pop 420ms var(--ai-ease) both;
  }

  .ai-launcher__badge.is-warn {
    background: var(--ai-warn);
  }

  .ai-launcher__label {
    padding: 8px 14px;
    border-radius: 999px;
    background: var(--ai-ink);
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.22);
  }

  .ai-label-enter-active,
  .ai-label-leave-active {
    transition: opacity 160ms var(--ai-ease), transform 220ms var(--ai-ease);
  }

  .ai-label-enter-from,
  .ai-label-leave-to {
    opacity: 0;
    transform: translateX(8px);
  }

  .ai-icon-enter-active,
  .ai-icon-leave-active {
    transition: opacity 140ms, transform 200ms var(--ai-ease);
  }

  .ai-icon-enter-from {
    opacity: 0;
    transform: rotate(-90deg) scale(0.6);
  }

  .ai-icon-leave-to {
    opacity: 0;
    transform: rotate(90deg) scale(0.6);
  }

  @media (max-width: 599px) {
    .ai-launcher {
      right: 16px;
      bottom: calc(80px + var(--app-fab-lift, 0px));
    }

    .ai-launcher--open {
      display: none;
    }

    .ai-launcher__label {
      display: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .ai-launcher__orb,
    .ai-launcher__ring,
    .ai-launcher__badge {
      animation: none !important;
    }
  }
</style>
